import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import UserCard from './UserCard';
import { Modal, Button, Form } from 'react-bootstrap';
import { Eye, EyeSlash, EnvelopeFill, LockFill, PersonFill, PhoneFill, BriefcaseFill, ExclamationTriangleFill } from 'react-bootstrap-icons';

// Import your slideshow images
import slide4 from '../img/slide4.jpg';
import slide2 from '../img/slide2.jpg';
import slide3 from '../img/slide3.jpg';

const SignUp = ({ setIsAuthenticated, setUserRole }) => {
  // Form states
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [position, setPosition] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [showError, setShowError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
useEffect(() => {
  if (error) {
    setShowError(true);
    const timer = setTimeout(() => {
      setShowError(false);
      setError('');
    }, 3000);
    return () => clearTimeout(timer);
  }
}, [error]);


  // Slideshow state
  const [slides, setSlides] = useState([
    { id: 0, className: 'active', image: slide4 },
    { id: 1, className: 'next', image: slide2 },
    { id: 2, className: 'prev', image: slide3 }
  ]);

  const navigate = useNavigate();

  // Slideshow effect
  useEffect(() => {
    const interval = setInterval(() => {
      setSlides(prev => {
        const newSlides = [...prev];
        // Rotate slides
        newSlides.forEach(slide => {
          if (slide.className === 'active') slide.className = 'prev';
          else if (slide.className === 'next') slide.className = 'active';
          else if (slide.className === 'prev') slide.className = 'next';
        });
        return newSlides;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Updated handleSignUp function with custom validation
  const handleSignUp = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError('');
  
    // Field validation
    const fields = { name, email, phone, position, password, confirmPassword };
    const emptyFields = Object.entries(fields).filter(([_, value]) => !value.trim());
    
    // General validation - all fields required
    if (emptyFields.length > 0) {
      setError('Please fill in all required fields');
      setIsLoading(false);
      return;
    }
  
    // Name validation - only letters and spaces
    if (!/^[a-zA-Z\s]+$/.test(name)) {
      setError('Full Name should only contain letters and spaces');
      setIsLoading(false);
      return;
    }
  
    // Email validation
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      setIsLoading(false);
      return;
    }
  
    // Phone validation - numbers only, at least 8 digits
    const cleanPhone = phone.replace(/\s/g, '');
    if (!/^\d+$/.test(cleanPhone) || cleanPhone.length < 8) {
      setError('Phone number should contain numbers only and be at least 8 digits');
      setIsLoading(false);
      return;
    }
  
    // Password validation
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setIsLoading(false);
      return;
    }
  
    if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
      setError('Password must contain both letters and numbers');
      setIsLoading(false);
      return;
    }
  
    if (/[^a-zA-Z0-9@#*]/.test(password)) {
      setError('Password may only contain letters, numbers, and @#* symbols');
      setIsLoading(false);
      return;
    }
  
    // Password match validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsLoading(false);
      return;
    }
  
    try {
     const response = await authAPI.adminSignUp({ 
      name: name.trim(),
      email: email.trim(),
      phone: cleanPhone,
      position: position.trim(),
      password: password.trim()
    });
    
      if (response.data.success) {
        setIsAuthenticated(true);
        setUserRole('admin');
        navigate('/home');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  return (
    <div className="signup-container">
      {/* Slideshow Background */}
      <div className="slideshow">
        {slides.map((slide) => (
          <div 
            key={slide.id}
            className={`slide ${slide.className}`}
            style={{ backgroundImage: `url(${slide.image})` }}
          />
        ))}
      </div>

      {/* Content Overlay */}
      <div className="content-overlay">
        <div className="user-card-wrapper">
          <div className="user-card-container">
            <UserCard 
              name="Admin Registration" 
              text="Create Admin Account" 
              icon="admin" 
              onClick={() => setShowModal(true)} 
            />
            
            <div className="futuristic-footer">
              <p className="futuristic-link-text">
                Already have an account?{' '}
                <Link to="/signin" className="futuristic-link">
                  Login here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sign Up Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="futuristic-modal">
        <Modal.Header closeButton>
          <Modal.Title>ADMIN REGISTRATION</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSignUp}>
            <Form.Group controlId="name" className="futuristic-input-group">
              <PersonFill className="futuristic-icon" />
              <Form.Control
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`futuristic-input ${name ? 'filled' : ''}`}
              />
              <Form.Label className="futuristic-label">Full Name</Form.Label>
            </Form.Group>

            <Form.Group controlId="email" className="futuristic-input-group">
              <EnvelopeFill className="futuristic-icon" />
              <Form.Control
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`futuristic-input ${email ? 'filled' : ''}`}
              />
              <Form.Label className="futuristic-label">Email Address</Form.Label>
            </Form.Group>

            <Form.Group controlId="phone" className="futuristic-input-group">
              <PhoneFill className="futuristic-icon" />
              <Form.Control
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={`futuristic-input ${phone ? 'filled' : ''}`}
              />
              <Form.Label className="futuristic-label">Phone Number</Form.Label>
            </Form.Group>

            <Form.Group controlId="position" className="futuristic-input-group">
              <BriefcaseFill className="futuristic-icon" />
              <Form.Control
                type="text"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                className={`futuristic-input ${position ? 'filled' : ''}`}
              />
              <Form.Label className="futuristic-label">Position</Form.Label>
            </Form.Group>

            <Form.Group controlId="password" className="futuristic-input-group">
              <LockFill className="futuristic-icon" />
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`futuristic-input ${password ? 'filled' : ''}`}
              />
              <Form.Label className="futuristic-label">Password</Form.Label>
              <Button 
                variant="link" 
                onClick={togglePasswordVisibility}
                className="futuristic-eye-btn"
              >
                {showPassword ? <EyeSlash /> : <Eye />}
              </Button>
            </Form.Group>

            <Form.Group controlId="confirmPassword" className="futuristic-input-group">
              <LockFill className="futuristic-icon" />
              <Form.Control
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`futuristic-input ${confirmPassword ? 'filled' : ''}`}
              />
              <Form.Label className="futuristic-label">Confirm Password</Form.Label>
              <Button 
                variant="link" 
                onClick={toggleConfirmPasswordVisibility}
                className="futuristic-eye-btn"
              >
                {showConfirmPassword ? <EyeSlash /> : <Eye />}
              </Button>
            </Form.Group>

           {showError && error && (
             <p className="futuristic-error" style={{ animation: 'fadeOut 0.5s ease 2.5s forwards' }}> <ExclamationTriangleFill className="mx-1" / >
                {error}
              </p>
            )}

            <Modal.Footer className="d-flex flex-column">
              <Button className='futuristic-btn' type="submit" disabled={isLoading}>
                {isLoading ? 'CREATING ACCOUNT...' : 'CREATE ACCOUNT'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SignUp;